import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronUp, Building, MapPin, User, Save, RotateCcw } from 'lucide-react';
import { loadSystemContext, saveSystemContext } from '../utils/systemContext';
import PropTypes from "prop-types";


debugger;
const FORM_FIELDS = {
  COMPANY_CODE: {
    key: 'companyCode',
    placeholder: 'Company Code',
    icon: Building,
    required: true
  },
  REGION_CODE: {
    key: 'regionCode',
    placeholder: 'Region Code',
    icon: MapPin,
    required: false
  },
  USER_CODE: {
    key: 'userCode',
    placeholder: 'User Code',
    icon: User,
    required: false
  }
};

const MESSAGES = {
  SUCCESS: 'Codes saved successfully!',
  ERROR: 'Please fill in Company Code',
  TITLE: 'System Context',
  SUBTITLE: 'Configure your context for better responses'
};
debugger;
const SystemContextForm = ({ onCodesChange, isCollapsed = false, initialContext = {} }) => {
  const [isExpanded, setIsExpanded] = useState(false);
    const [formData, setFormData] = useState({
        companyCode: initialContext.companyCode || '',
        regionCode: initialContext.regionCode || '',
        userCode: initialContext.userCode || ''
    });

    useEffect(() => {
        if (initialContext && Object.keys(initialContext).length > 0) {
            setFormData(initialContext);
        }
    }, [initialContext]);


  const [hasChanges, setHasChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load saved codes on component mount
  useEffect(() => {
    const savedData = loadSystemContext();
    setFormData(savedData);
  }, []);

  // Check for changes
  useEffect(() => {
    const currentData = loadSystemContext();

    const hasFormChanges = Object.keys(FORM_FIELDS).some(key => {
      const fieldKey = FORM_FIELDS[key].key;
      return formData[fieldKey] !== currentData[fieldKey];
    });

    setHasChanges(hasFormChanges);
  }, [formData]);

  // Handle input changes
  const handleInputChange = useCallback((fieldKey, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!formData.companyCode.trim()) {
      // You could emit an event or use a toast notification here
      console.warn(MESSAGES.ERROR);
      return;
    }

    setIsSubmitting(true);
     
    try {
      // Simulate API call delay for better UX
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Save to localStorage using utility function
      saveSystemContext(formData);
      
      // Notify parent component
      onCodesChange?.(formData);
      
      setHasChanges(false);
      console.log(MESSAGES.SUCCESS);
    } catch (error) {
      console.error('Error saving codes:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, onCodesChange]);

  // Handle reset
  const handleReset = useCallback(() => {
    const savedData = loadSystemContext();
    setFormData(savedData);
    setHasChanges(false);
  }, []);

  // Toggle expanded state
  const toggleExpanded = useCallback(() => {
    // Don't allow expansion when sidebar is collapsed
    if (isCollapsed) return;
    setIsExpanded(prev => !prev);
  }, [isCollapsed]);

  return (
    <div className="mt-auto p-3 border-t border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div 
        className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} p-2 rounded-md transition-colors ${
          isCollapsed 
            ? 'cursor-default' 
            : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700'
        }`}
        onClick={toggleExpanded}
        title={isCollapsed ? `${MESSAGES.TITLE} - Expand sidebar to configure` : undefined}
      >
        <div className="flex items-center gap-2">
          <Building size={16} className="text-blue-500" />
          {!isCollapsed && (
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {MESSAGES.TITLE}
            </span>
          )}
        </div>
        {!isCollapsed && (
          <div className="flex items-center gap-1">
            {hasChanges && (
              <div className="w-2 h-2 bg-orange-500 rounded-full" />
            )}
            {isExpanded ? (
              <ChevronDown size={16} className="text-gray-500" />
            ) : (
              <ChevronUp size={16} className="text-gray-500" />
            )}
          </div>
        )}
      </div>

      {/* Form Content */}
      {isExpanded && !isCollapsed && (
        <div className="mt-2 space-y-3 animate-in slide-in-from-top-2 duration-200">
          <p className="text-xs text-gray-500 dark:text-gray-400 px-2">
            {MESSAGES.SUBTITLE}
          </p>
          
          <div className="space-y-2">
            {Object.entries(FORM_FIELDS).map(([key, field]) => {
              const IconComponent = field.icon;
              return (
                <div key={field.key} className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <IconComponent size={14} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder={field.placeholder}
                    value={formData[field.key]}
                    onChange={(e) => handleInputChange(field.key, e.target.value)}
                    className={`w-full pl-9 pr-3 py-2 text-sm border rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      field.required && !formData[field.key] 
                        ? 'border-red-300 dark:border-red-600' 
                        : 'border-gray-300 dark:border-gray-600'
                    } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100`}
                    required={field.required}
                  />
                </div>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !hasChanges}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                isSubmitting || !hasChanges
                  ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={14} />
                  Save
                </>
              )}
            </button>
            
            {hasChanges && (
              <button
                onClick={handleReset}
                className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                title="Reset changes"
              >
                <RotateCcw size={14} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

SystemContextForm.propTypes = {
    onCodesChange: PropTypes.func.isRequired,     
    isCollapsed: PropTypes.bool,            
    initialContext: PropTypes.shape({           
        companyCode: PropTypes.string,
        regionCode: PropTypes.string,
        userCode: PropTypes.string,
    }),
};

SystemContextForm.defaultProps = {
    isCollapsed: false,
    initialContext: {
        companyCode: "",
        regionCode: "",
        userCode: "",
    },
};

export default SystemContextForm;
